import * as React from "react";
import { SkeletonElement } from "./skeleton";
import { cn } from "@/lib/utils";
import { buttonInteractionPatterns } from "@/styles/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-cede-primary cursor-pointer hover:cursor-pointer active:cursor-pointer",
  {
    variants: {
      variant: {
        // main variant
        "main": `bg-cede-button-primary text-cede-text-primary rounded-button ${buttonInteractionPatterns.main()}`,
        // Primary variant - matches ContainedButton primary
        "primary": `bg-cede-button-primary text-cede-text-primary rounded-button ${buttonInteractionPatterns.primary()}`,
        // Unlink variant - darker contrast background for unlink actions
        "unlink": `bg-[var(--color-contrast-shade-primary-16)] text-cede-primary rounded-button ${buttonInteractionPatterns.unlink()}`,
        // Secondary variant - matches ContainedButton secondary
        "secondary": `bg-cede-button-primary/30 text-cede-text-primary rounded-button ${buttonInteractionPatterns.secondary()}`,
        // Tertiary variant - matches ContainedButton tertiary
        "tertiary": `bg-cede-button-primary text-cede-text-primary rounded-button ${buttonInteractionPatterns.tertiary()}`,
        // Transparent variant - matches TransparentButton
        "transparent": `bg-transparent text-cede-primary rounded-button ${buttonInteractionPatterns.transparent()}`,
        // Transparent with border - matches TransparentButton with showBorder
        "transparent-bordered": `bg-transparent text-cede-primary border border-cede-primary rounded-button disabled:border-cede-border-container disabled:text-cede-text-secondary ${buttonInteractionPatterns.transparentBordered()}`,
        // Destructive variant
        "destructive": `bg-cede-error/36 text-cede-error rounded-button ${buttonInteractionPatterns.destructive()}`,
        // Ghost variant
        "ghost": `bg-transparent text-cede-text-primary rounded-button ${buttonInteractionPatterns.ghost()}`,
        // Link variant
        "link": `text-cede-primary underline-offset-4 ${buttonInteractionPatterns.link()}`,
        // Outline variant
        "outline": `bg-transparent text-cede-text-primary border border-cede-hr rounded-button ${buttonInteractionPatterns.outline()}`,
        // Body text variant
        "body-text": `text-cede-text-secondary !font-light ${buttonInteractionPatterns.bodyText()}`,
      },
      size: {
        // Matches heightMap from utils.ts
        small: "h-[36px] px-3 btn-medium", // Uses btn-medium typography class
        medium: "h-[42px] px-4 btn-large", // Uses btn-large typography class
        large: "h-[50px] px-6 btn-large", // Uses btn-large typography class
        icon: "size-9 p-0",
      },
      rounded: {
        true: "rounded-[400px]", // Uses button rounded radius from theme
        false: "rounded-button", // Uses default button radius from theme
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
      rounded: false,
    },
  },
);

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, rounded, asChild = false, loading, leftIcon, rightIcon, children, disabled, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, rounded }),
          {
            // Loading state - use skeleton colors
            "!bg-cede-skeleton !text-cede-text-disabled": loading,
            // Disabled state - use component colors
            "disabled:bg-cede-bg-component disabled:text-cede-text-disabled":
              disabled && !loading && variant !== "transparent-bordered",
          },
          className,
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <SkeletonElement className="absolute inset-0 rounded-button" />}
        <div className={cn("flex items-center gap-2")}>
          {leftIcon}
          {children}
          {rightIcon}
        </div>
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
